import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import {
  CubeSchemaTables,
  DRIFT_CHAT_OPPORTUNITY_OVERVIEW,
} from 'cube/constants'

import { useTableState } from '@driftt/dds'
import {
  type CubeQuery,
  CubeQueryBuilder,
  resultSetAsTableData,
  useCubeQuery,
} from '@driftt/dds.reports'

import { IntegrationAPI } from 'api/index'
import { resolveCubeKPIResults } from 'components/KPICard'
import {
  OpportunitiesBaseQuery,
  OpportunitiesTableRow,
  OpportunityCountBaseQuery,
  OpportunityDetailMetric,
} from 'layouts/RevenueReport'
import { useOpportunitiesStore } from 'layouts/RevenueReport/Opportunities/Opportunities.context'
import { type IOpportunitiesTableDataResult } from 'layouts/RevenueReport/Opportunities/Opportunities.types'

export const useOpportunities = ({
  orderBy,
}): IOpportunitiesTableDataResult => {
  const {
    store: { startDate, endDate, filters },
  } = useOpportunitiesStore()

  const { tableState, handleTableStateChange } =
    useTableState<OpportunitiesTableRow>({
      defaultPagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    })

  const opportunityCountQuery = new CubeQueryBuilder({
    query: OpportunityCountBaseQuery,
  })
    .withName(`OpportunityCount-Opportunities-${startDate}-${endDate}`)
    .withQueryName(`OpportunityCount-Opportunities-${startDate}-${endDate}`)
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .withFilters(filters)
    .build()

  const {
    data: opportunityCountResultSet,
    isLoading: isLoadingOpportunityCount,
    isError: isErrorTotaOpportunityCount,
  } = useCubeQuery({
    query: opportunityCountQuery,
  })

  const getOpportunityDataCubeQuery: CubeQuery = new CubeQueryBuilder({
    query: OpportunitiesBaseQuery,
  })
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .withName(
      `RevenueOpportunitiesQuery-${tableState.pagination.pageIndex}-${tableState.pagination.pageSize}`
    )
    .withLimit(tableState.pagination.pageSize)
    .withOffset(
      tableState.pagination.pageIndex * tableState.pagination.pageSize
    )
    .withFilters(filters)
    .withOrder(orderBy)
    .build()

  const { data, isLoading, isError } = useCubeQuery({
    query: getOpportunityDataCubeQuery,
  })

  const opportunitiesData: OpportunitiesTableRow[] = useMemo(() => {
    const tableRows = resultSetAsTableData(data) as OpportunityDetailMetric[]

    return tableRows.map((row) => {
      return {
        ...row[CubeSchemaTables.DRIFT_CHAT_OPPORTUNITY_OVERVIEW],
        ...row[CubeSchemaTables.MEETINGS],
      }
    })
  }, [data])

  const totalRowCount = useMemo(
    () =>
      resolveCubeKPIResults(
        opportunityCountResultSet,
        'DriftChatOpportunityOverview.count'
      ),
    [opportunityCountResultSet]
  )

  return useMemo(
    () => ({
      data: opportunitiesData,
      totalRowCount,
      isLoading,
      isError,
      isLoadingOpportunityCount,
      isErrorTotaOpportunityCount,
      pagination: tableState.pagination,
      handleTableStateChange,
      getOpportunityDataCubeQuery,
    }),
    [
      opportunitiesData,
      totalRowCount,
      isLoading,
      isError,
      isLoadingOpportunityCount,
      isErrorTotaOpportunityCount,
      tableState.pagination,
      handleTableStateChange,
      getOpportunityDataCubeQuery,
    ]
  )
}

export function useFetchSalesForceDomainAPI() {
  const {
    data: domain,
    isLoading: loading,
    isError: error,
  } = useQuery({
    queryFn: () =>
      IntegrationAPI.get(`integrations/salesforce`)
        .then(({ data }) => {
          const {
            settings: { namedStrings },
          } = data

          const { value = '' } = namedStrings.find(
            (e: { name: string; value: string }) => e?.name === 'domain'
          )
          return value
        })
        .catch((err) => {
          console.error(err)
          throw error
        }),
  })

  return { domain, error, loading }
}

export default useOpportunities
